import { isEmpty } from "lodash-es";
import { ensureArray } from "types";

import { get, IHttpRequestConfig, post, put, remove } from "@/browser";
import { applyHeaders, extractDataFromResponse, parseResponse } from "@/core/api";
import { compose, IURLPageConfig, IURLSearchParams, IURLSortConfig } from "@/core/url";
import { IPartialPageableConsultantProfile, pageableConsultantProfileType } from "@/data/consultant-profiles";
import {
  JobRequestListDto,
  JobRequestListDtoPageable,
  JobRequestV2,
  JobRequestV2Pageable,
} from "@/data/job-requests/types/JobRequest";

import { getSkillById, ISkill } from "../skills";
import { IJobRequestFilter, IJobRequestFilterDto, jobRequestFilterType } from "./filters";
import {
  IJobRequest,
  IJobRequestConsent,
  IPartialPageableJobRequest,
  jobRequestConsentType,
  jobRequestType,
  pageableJobRequestType,
  pageableShortJobRequestType,
} from "./types-ensure";

export const API = {
  list: "/api/job-requests/v2",
  listV1: "/api/job-requests",
  publicList: "/api/public/job-requests",
  get: "/api/job-requests/{jobRequestId}",
  getPublic: "/api/public/job-requests/{jobRequestId}",
  getConsultants: "/api/job-requests/{jobRequestId}/consultant-profiles",
  getTopMatchingConsultants: "/api/job-requests/{jobRequestId}/recommended-profiles",
  recommended: "/api/job-requests/recommended",
  recommendedByProfile: "/api/job-requests/recommended/profile/{profileId}",
  similar: "/api/public/job-requests/{jobRequestId}/similar",
  consent: "/api/job-requests/{jobRequestId}/consent",
  saveFilter: "/api/job-requests/filters",
  listFilter: "/api/job-requests/filters",
  deleteFilter: "/api/job-requests/filters/{id}",
  updateFilter: "/api/job-requests/filters/{id}",
  setFavourite: "/api/job-requests/{jobRequestId}/favourite",
  deleteFavourtie: "/api/job-requests/{jobRequestId}/favourite",
  mostRecent: "/api/job-requests/most-recent",
  ogImage: "/api/public/job-requests/{jobRequestId}/og-image",
};

export interface IFetchJobRequestsListParams extends IURLSearchParams {
  companyId?: number;
}

const mapFlatJobRequestToListDto = (flatData: JobRequestV2[]): JobRequestListDto[] => {
  return flatData.map(flatJR => {
    return {
      id: flatJR.id,
      title: flatJR.title,
      systemId: flatJR.systemId,
      firstDayOfApplications: flatJR.firstDayOfApplications,
      client: {
        name: flatJR.name,
        logo: {
          publicUrl: flatJR.publicUrl,
        },
      },
      legalEntityClient: {
        id: 1,
        name: flatJR.clientLegalEntityName ?? "",
        publicProfileId: null,
        aboutUs: null,
        publicProfileSlug: null,
        aboutUsFlavorText: null,
        country: null,
        backgroundImagePublicUrl: null,
        backgroundImageSystemId: null,
        logoPublicUrl: flatJR.clientLegalEntityLogo,
        vatNumber: null,
        logoSystemId: null,
        publicProfileName: null,
      },
      administratorLegalEntityClient: {
        id: 1,
        name: flatJR.administratorLegalEntityName ?? "",
        publicProfileId: null,
        aboutUs: null,
        publicProfileSlug: null,
        aboutUsFlavorText: null,
        country: null,
        backgroundImagePublicUrl: null,
        backgroundImageSystemId: null,
        logoPublicUrl: flatJR.administratorLegalEntityLogo,
        vatNumber: null,
        logoSystemId: null,
        publicProfileName: null,
      },
      origin: {
        name: flatJR.originServiceName,
        details: {
          logo: flatJR.logo,
        },
      },
      rate: {
        maxRate: flatJR.maxRate,
        clientRateType: flatJR.clientRateType,
        currency: flatJR.currency,
      },
      locations: [
        {
          city: flatJR.city,
          countryCode: flatJR.countryCode,
          name: flatJR.city ?? undefined,
        },
      ],
      public: flatJR.public,
      remoteness: flatJR.remoteness,
      promoteClient: flatJR.promoteClient,
      favourite: flatJR.favourite,
    };
  });
};

export const fetchJobRequestList = (params: IFetchJobRequestsListParams = {}, options: IHttpRequestConfig = {}) => {
  const { companyId, ...searchParams } = params;

  const url = compose(API.list, {}, searchParams);
  return get<JobRequestListDtoPageable>(url, applyHeaders({ companyId }, options))
    .then(parseResponse<JobRequestV2Pageable>(JobRequestV2Pageable))
    .then(pageable => {
      return {
        ...pageable,
        content: mapFlatJobRequestToListDto(pageable.content),
      };
    });
};

export const fetchJobRequestListV1 = (params: IFetchJobRequestsListParams = {}, options: IHttpRequestConfig = {}) => {
  const { companyId, ...searchParams } = params;

  const url = compose(API.listV1, {}, searchParams);
  return get<IPartialPageableJobRequest>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableJobRequestType);
};

export const fetchPublicJobRequestList = (
  params: IFetchJobRequestsListParams = {},
  options: IHttpRequestConfig = {},
) => {
  const { companyId, ...searchParams } = params;

  const url = compose(API.publicList, {}, searchParams);

  return get<IPartialPageableJobRequest>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableJobRequestType);
};

export interface IFetchJobRequestParams {
  jobRequestId: number;
  companyId?: number;
}

export const fetchJobRequest = (params: IFetchJobRequestParams, options?: IHttpRequestConfig) => {
  const { companyId, ...pathParams } = params;

  const url = compose(API.get, pathParams);
  return get<IJobRequest>(url, applyHeaders({ companyId }, options)).then(extractDataFromResponse).then(jobRequestType);
};

export const fetchPublicJobRequest = (params: IFetchJobRequestParams, options?: IHttpRequestConfig) => {
  const { companyId, ...pathParams } = params;

  const url = compose(API.getPublic, pathParams);
  return get<IJobRequest>(url, applyHeaders({ companyId }, options)).then(extractDataFromResponse).then(jobRequestType);
};

export interface IFetchJobRequestConsultantProfilesParams {
  jobRequestId: number;
  companyId?: number;
}

export const fetchJobRequestConsultantProfiles = (
  params: IFetchJobRequestConsultantProfilesParams,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...pathParams } = params;

  const url = compose(API.getConsultants, pathParams);

  return get<IPartialPageableConsultantProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableConsultantProfileType);
};

interface IFetchJobRequestTopMatchingConsultantProfilesParams
  extends IFetchJobRequestConsultantProfilesParams,
    Partial<IURLPageConfig>,
    Partial<IURLSortConfig> {}

export const fetchJobRequestTopMatchingConsultantProfiles = (
  params: IFetchJobRequestTopMatchingConsultantProfilesParams,
  options?: IHttpRequestConfig,
) => {
  const { companyId, size = 3, page = 0, sortBy, order, ...pathParams } = params;

  const url = compose(
    API.getTopMatchingConsultants,
    {
      ...pathParams,
    },
    {
      page,
      size,
      sortConfig: [{ sortBy, order }],
    },
  );

  return get<IPartialPageableConsultantProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableConsultantProfileType);
};

export interface IFetchJobRequestConsentsParams extends IFetchJobRequestConsultantProfilesParams {}

export const fetchJobRequestConsent = (params: IFetchJobRequestConsentsParams, options?: IHttpRequestConfig) => {
  const { companyId, jobRequestId } = params;
  const url = compose(API.consent, { jobRequestId });
  return get<IJobRequestConsent | null>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(v => {
      // TODO move it
      return isEmpty(v) ? null : jobRequestConsentType(v as IJobRequestConsent);
    });
};

interface IRecommendedJobRequestsParams extends Pick<IFetchJobRequestParams, "companyId">, Partial<IURLPageConfig> {}

export const fetchRecommendedJobRequests = (params: IRecommendedJobRequestsParams, options?: IHttpRequestConfig) => {
  const { companyId, size = 3, page = 0, ...pathParams } = params;

  const url = compose(API.recommended, {
    ...pathParams,
    page,
    size,
  });
  return get<IPartialPageableJobRequest>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableShortJobRequestType);
};

interface IRecommendedJobRequestsByProfileParams
  extends Pick<IFetchJobRequestParams, "companyId">,
    Partial<IURLPageConfig>,
    IURLSearchParams {
  profileId: number;
}

export const fetchRecommendedJobRequestsByProfile = (
  params: IRecommendedJobRequestsByProfileParams,
  options?: IHttpRequestConfig,
) => {
  const { companyId, size = 3, page = 0, profileId, ...searchParams } = params;

  const url = compose(API.recommendedByProfile, { profileId }, { ...searchParams, page, size });
  return get<IPartialPageableJobRequest>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableJobRequestType);
};

interface ISimilarJobRequestsParams extends Partial<IURLPageConfig> {
  jobRequestId: number;
}

export const fetchSimilarJobRequests = (params: ISimilarJobRequestsParams, options?: IHttpRequestConfig) => {
  const { size = 3, page = 0, ...pathParams } = params;

  const url = compose(API.similar, {
    ...pathParams,
    page,
    size,
  });
  return get<IPartialPageableJobRequest>(url, options).then(extractDataFromResponse).then(pageableShortJobRequestType);
};

export interface IPostJobRequestFilterParams {
  companyId?: number;
}

export const postJobRequestFilter = (
  params: IPostJobRequestFilterParams,
  data: IJobRequestFilterDto,
  options?: IHttpRequestConfig,
) => {
  const { companyId } = params;
  const url = compose(API.saveFilter);

  return post<IJobRequestFilter>(url, data, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(jobRequestFilterType);
};

export interface IFetchJobRequestFiltersParams {
  countMatchingElements?: boolean;
  companyId?: number;
  shouldPopulateSkills?: any;
}

function populateSkills(filters: IJobRequestFilter[]) {
  const completedSkills: {
    [key: string]: ISkill[];
  } = {};
  const promises: Promise<void>[] = [];

  filters.forEach(filter => {
    const filterId = filter.id;
    const skillIds = filter.items.skillIds ?? [];

    if (skillIds.length > 0) {
      promises.push(
        getSkillById(skillIds).then(response => {
          completedSkills[filterId] = response;
        }),
      );
    }
  });

  return Promise.all(promises).then(() => {
    const updatedResponse = filters.map(filter => {
      if (completedSkills[filter.id]) {
        filter.items.skills = completedSkills[filter.id];
      }
      return filter;
    });

    return updatedResponse;
  });
}

export const fetchJobRequestFilters = (params: IFetchJobRequestFiltersParams, options?: IHttpRequestConfig) => {
  const { companyId, shouldPopulateSkills, ...restParams } = params;
  const url = compose(API.listFilter, {}, restParams);

  return get<IJobRequestFilter[]>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(response => (shouldPopulateSkills ? populateSkills(response) : response))
    .then(ensureArray(jobRequestFilterType));
};

export interface IDeleteJobRequestFilterParams {
  id: IJobRequestFilter["id"];
  companyId?: number;
}

export const deleteJobRequestFilter = (params: IDeleteJobRequestFilterParams, options?: IHttpRequestConfig) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.deleteFilter, restParams);

  return remove(url, {}, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};

export interface IPutJobRequestFilterParams {
  id: IJobRequestFilter["id"];
  companyId?: number;
}

export const putJobRequestFilter = (
  params: IPutJobRequestFilterParams,
  data: IJobRequestFilter,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.updateFilter, restParams);

  return put<IJobRequestFilter>(url, data, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};

export interface IDeleteFavouriteParams {
  jobRequestId: number;
  companyId?: number;
}

export const setFavouriteJobRequest = (params: IDeleteFavouriteParams, options?: IHttpRequestConfig) => {
  const url = compose(API.setFavourite, params);
  return post(url, {}, applyHeaders({ companyId: params.companyId }, options));
};

export interface IDeleteFavouriteParams {
  jobRequestId: number;
  companyId?: number;
}

export const deleteFavouriteJobRequest = (params: IDeleteFavouriteParams, options?: IHttpRequestConfig) => {
  const url = compose(API.setFavourite, params);
  return remove(url, {}, applyHeaders({ companyId: params.companyId }, options));
};

export interface IFetchMostRecentJobRequestsParams {
  companyId?: number;
}

export const fetchMostRecentJobRequests = (
  params: IFetchMostRecentJobRequestsParams,
  options: IHttpRequestConfig = {},
) => {
  const { companyId } = params;

  const url = compose(API.mostRecent, {});

  return get<IJobRequest[]>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(ensureArray(jobRequestType));
};
